import type { StatusTone } from '@vnetwork/ui/data';
// Mirrors `DomainStatus` of `src/api/manticore/domains.ts` (a string union that also accepts any string).
type DomainStatus = 'active' | 'deploying' | 'suspended' | 'disabled' | 'pending' | string;

// Domain lifecycle → StatusPill tone + label. Ported from the legacy
// DOMAIN_STATES mapping in External.SwiftFederation/ManageDomains/DomainList.
export const DOMAIN_STATUS_TONE: Record<string, StatusTone> = {
  active: 'success',
  deploying: 'info',
  suspended: 'danger',
  disabled: 'muted',
  pending: 'warning',
};

export const DOMAIN_STATUS_LABEL: Record<string, string> = {
  active: 'Enabled',
  deploying: 'Deploying',
  suspended: 'Suspended',
  disabled: 'Disabled',
  pending: 'Pending',
};

function domainStatusTone(status: DomainStatus | undefined): StatusTone {
  return DOMAIN_STATUS_TONE[String(status ?? '').toLowerCase()] ?? 'muted';
}

export function domainStatusLabel(status: DomainStatus | undefined): string {
  const key = String(status ?? '').toLowerCase();
  return DOMAIN_STATUS_LABEL[key] ?? (status ? String(status) : 'Unknown');
}

/** What a domain row/detail says about being on: vEdge answers `enabled`, @mgmt a lifecycle `status`. */
interface DomainStateInput {
  enabled?: unknown;
  status?: DomainStatus;
}

/** Enabled or not. A boolean `enabled` wins; without one the lifecycle status decides (active/deploying). */
export function isDomainEnabled(d: DomainStateInput | null | undefined): boolean {
  if (typeof d?.enabled === 'boolean') return d.enabled;
  const status = String(d?.status ?? '').toLowerCase();
  return status === 'active' || status === 'deploying';
}

/**
 * The badge of a domain. `enabled` drives it (an enabled domain whose registry status is still
 * "pending" or unknown reads Enabled, not Unknown); a suspension and an in-flight deploy are the two
 * lifecycle states that say more than on/off, so they stay. With no `enabled` it is the status.
 */
export function domainBadge(d: DomainStateInput | null | undefined): { tone: StatusTone; label: string } {
  const status = String(d?.status ?? '').toLowerCase();
  if (typeof d?.enabled !== 'boolean') return { tone: domainStatusTone(d?.status), label: domainStatusLabel(d?.status) };
  if (status === 'suspended') return { tone: 'danger', label: DOMAIN_STATUS_LABEL.suspended };
  if (!d.enabled) return { tone: 'muted', label: DOMAIN_STATUS_LABEL.disabled };
  if (status === 'deploying') return { tone: 'info', label: DOMAIN_STATUS_LABEL.deploying };
  return { tone: 'success', label: DOMAIN_STATUS_LABEL.active };
}

/** "5 minutes ago" for a timestamp; the empty string when it is missing or not a date. */
export function relativeTime(value: unknown, now: number = Date.now()): string {
  const t = typeof value === 'string' || typeof value === 'number' ? Date.parse(String(value)) : NaN;
  if (!Number.isFinite(t) || t <= 0) return '';
  const secs = Math.max(0, Math.round((now - t) / 1000));
  if (secs < 60) return 'just now';
  const units: [number, string][] = [[60, 'minute'], [3600, 'hour'], [86400, 'day'], [2592000, 'month'], [31536000, 'year']];
  let n = Math.floor(secs / 60);
  let unit = 'minute';
  for (const [size, name] of units) {
    if (secs >= size) {
      n = Math.floor(secs / size);
      unit = name;
    }
  }
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
}

/** The URL behind the "open" link: always https; a wildcard names no single host, so it has none. */
export function domainOpenUrl(domain: string | undefined): string | undefined {
  const host = String(domain ?? '').trim();
  return host && !host.includes('*') ? `https://${host}` : undefined;
}

/**
 * Resolve a status tone to a REAL @vnetwork/ui v2 color token. Use for
 * `Stat dotColor`, icon `color`, etc. — never hand-write `--vn-tone-*` (those
 * don't exist in the design system and silently fall back to hard-coded hex
 * that breaks dark mode).
 *
 * @param variant 'solid' for swatches/icons (default), 'text' for readable text.
 */
export function toneColor(tone: StatusTone, variant: 'solid' | 'text' = 'solid'): string {
  switch (tone) {
    case 'success':
      return `var(--vn-success-${variant})`;
    case 'info':
      return `var(--vn-info-${variant})`;
    case 'warning':
      return `var(--vn-warning-${variant})`;
    case 'danger':
      return `var(--vn-danger-${variant})`;
    default:
      return variant === 'text' ? 'var(--vn-fg-muted)' : 'var(--vn-fg-subtle)';
  }
}

/** "Configured / Not configured" chip — shared across Cache, SSL, etc. */
export function configTone(isConfigured: boolean): StatusTone {
  return isConfigured ? 'success' : 'muted';
}
export function configLabel(isConfigured: boolean): string {
  return isConfigured ? 'Configured' : 'Not configured';
}

/** Domains that are mid-transition can't be deleted. */
export function isDomainDeletable(status: DomainStatus | undefined): boolean {
  return !['deploying', 'provisioning', 'issuing_cert'].includes(String(status ?? ''));
}

/** Strict UUID — FE only ever sends UUIDs to the BE (dots in FQDNs broke SPA routing). */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Date formatters are centralized in @/lib/format-date (which also rejects
// backend zero-dates). Re-exported so existing './shared' import sites work.
export { formatDate, formatDateTime } from '@vnetwork/margarita-core/utils';

/** The CNAME the backend issued (`cname_target`, or the list's `cname`); empty when there is none. Never made up. */
export function cnameOf(d: { cname_target?: unknown; cname?: unknown } | null | undefined): string {
  return String(d?.cname_target || d?.cname || '').trim();
}

/** WAF entitlement of a domain object: `org_waf_enabled`, and `org_max_waf_domains` (0 or absent = unlimited). */
export function wafEntitlement(d: Record<string, unknown> | null | undefined): { allowed: boolean; limit?: number } {
  const max = d?.org_max_waf_domains;
  return { allowed: d?.org_waf_enabled === true, limit: typeof max === 'number' && max > 0 ? max : undefined };
}
